import { ICartPage, IEvent, IHost } from "@Interfaces";
import { getCartGtagData } from "@Modules/cartPage/utils/getCartGtagData";
import {
    evaluateHideAddToCartCta,
    getCartData,
} from "@Modules/eventPage/utils/cart";
import { getEventCurrency } from "@Modules/eventPage/utils/getEventCurrency";
import { handleDisabledCtaClick } from "@Modules/eventPage/utils/handleDisabledCtaClick";
import { handleEventStarted } from "@Modules/eventPage/utils/handleEventStarted";
import { useNeedUserInputToProceed } from "@Modules/eventPage/utils/useNeedUserInputToProceed";
import {
    EVENT_ENUM,
    ROUTES,
    SELECTORS,
    isBrowser,
    useEventDataOfSelectedEvent,
} from "@Utils";
import { canAddItemToCart, setSessionStorageCart } from "@Utils/cart";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { postAnalytics } from "repoV2/features/Common/Analytics/Analytics.utils";
import { ANALYTICS_EVENTS } from "repoV2/features/Common/Analytics/Analytics.constants";
import { useInstalmentsDetailsContext } from "../PaymentPlan/utils/InstalmentsDetailsContext";

export const useAddToCartCtaButton = ({
    disablePayment,
    scheduleSelectorRef,
    addToCartCallback,
    closeModal,
    isCtaDisabled,
    dynamicLink,
    isRebooking,
    userFlow,
    hostName,
}: {
    disablePayment?: boolean;
    scheduleSelectorRef: React.RefObject<HTMLDivElement>;
    addToCartCallback: ((data: ICartPage.ICart) => void) | undefined;
    closeModal: () => void;
    isCtaDisabled: boolean;
    dynamicLink?: string;
    isRebooking: boolean;
    userFlow?: "cart edit" | "cart add";
    hostName: string;
}) => {
    const router = useRouter();
    const hostData = useSelector(SELECTORS.hostData) || ({} as IHost.IHostData);
    const { event, eventData = {} as IEvent.IEventData } =
        useEventDataOfSelectedEvent();
    const { selectedPaymentType, instalments } = useInstalmentsDetailsContext();

    const { eventCurrency, eventCurrencySymbol } = getEventCurrency({
        eventData,
    });

    const {
        title: offeringTitle,
        type: offeringType,
        currency_name: currencyName,
        price_per_head: offeringPrice,
    } = eventData ?? {};

    const {
        discountCode,
        discountData,
        appliedDiscountCodeType,
        customerEmail: loyaltyDiscountEmail,
    } = event.discount;

    const {
        needUserInputToProceedData: {
            needUserInputToProceed,
            messageForUserToProceed,
        },
    } = useNeedUserInputToProceed();

    const {
        paymentInfo: { slots, quantity, numberOfCustomers },
    } = event;

    const cartData = getCartData();

    const hideComponent = evaluateHideAddToCartCta({
        hostData,
        disablePayment,
        dynamicLink,
        isRebooking,
    });
    const ctaText = userFlow === "cart edit" ? "Update Cart" : "Add to Cart";
    const onClick = () => {
        if (isCtaDisabled) {
            handleDisabledCtaClick({
                disablePayment,
                event,
                eventData,
                scheduleSelectorRef,
                needUserInputToProceed,
                messageForUserToProceed,
            });
            return;
        }

        if (numberOfCustomers > 1) {
            alert("Can't use cart when booking for multiple users");
            return;
        }

        handleEventStarted({ eventData, selectedSlots: slots });

        const cartCurrencySymbol =
            cartData?.currency?.currencySymbol || eventCurrencySymbol;

        const cartCurrencyString =
            cartData?.currency?.currencyString || eventCurrency;

        const newCartData: ICartPage.ICart = {
            ...cartData,
            listingMap: {
                ...cartData.listingMap,
                [eventData.uuid]: {
                    quantity,
                    eventData,
                    slots,
                    discount: {
                        discountData,
                        discountCode,
                        appliedDiscountCodeType,
                        loyaltyDiscountEmail,
                    },
                    billingDisplayFormatState: eventData.gst_summary_type,
                    selectedPaymentType,
                    instalments,
                },
            },
            currency: {
                currencySymbol: cartCurrencySymbol,
                currencyString: cartCurrencyString,
            },
        };

        if (!canAddItemToCart({ cartCurrencySymbol, eventData })) {
            alert(
                `You can only add item with same currency type to cart.\n\nCurrently you have ${cartCurrencyString} item(s) in cart. You can add more ${cartCurrencyString} items.`
            );
            return;
        }

        setSessionStorageCart(newCartData);
        addToCartCallback?.(newCartData);

        closeModal();

        postAnalytics(ANALYTICS_EVENTS.ADD_TO_CART, {
            sub_domain: hostName,
            content_name: offeringTitle,
            content_category: EVENT_ENUM[offeringType].TITLE,
            ...getCartGtagData(),
            currency: currencyName,
            value: offeringPrice,
        });

        if (router.pathname !== `/${ROUTES.CART.filePath}` && isBrowser()) {
            window.location.href = ROUTES.CART.urlPath;
        }
    };

    return {
        hideComponent,
        ctaText,
        onClick,
    };
};
